import Calendar from "react-calendar";
import { isNil } from "lodash";
import DayInput from "./DayInput";
import type { OptionPrices, PriceListPriceValue } from "./updateOptionPrices";
import dayjs, { ISO_DATE_FORMAT } from "../../../dayjs";
import { PriceMode } from "./PriceModeSelector";
import { assertUnreachable } from "../../../misc/utils";
import type { FC } from "react";
import type { Dayjs } from "dayjs";

function dateToIsoDate(dateObj: Date): string {
  return dayjs(dateObj).format(ISO_DATE_FORMAT);
}

function pricesForMode(productOption: OptionPrices, priceMode: PriceMode) {
  switch (priceMode) {
    case PriceMode.SELLING_PRICE:
      return productOption.selling;
    case PriceMode.PURCHASE_PRICE:
      return productOption.purchase;
    case PriceMode.FIT_PRICE:
      return productOption.fit;
    default:
      assertUnreachable(priceMode, "Unexpected price mode");
  }
}

interface Props {
  readonly priceMode: PriceMode;
  readonly prices: OptionPrices;
  readonly maxDate?: Dayjs;
  readonly sellingPricesReadOnly?: boolean;
  readonly onDaysValueChange: (days: string[], value: PriceListPriceValue) => void;
}

const OptionPricesCalendar: FC<Props> = ({ priceMode, prices, maxDate, sellingPricesReadOnly, onDaysValueChange }) => {
  const pricesOfType = pricesForMode(prices, priceMode);
  const readOnly = priceMode === PriceMode.SELLING_PRICE && !!sellingPricesReadOnly;

  const renderDayInput = ({ date, view }: { date: Date; view: string }) => {
    if (view === "month") {
      const day = dateToIsoDate(date);
      const priceForDate = pricesOfType[day];
      const value = isNil(priceForDate) ? "ON_DEMAND" : priceForDate;
      return <DayInput day={day} value={value} disabled={readOnly} onChange={(v) => onDaysValueChange([day], v)} />;
    }
    return null;
  };

  return <Calendar tileContent={renderDayInput} maxDate={maxDate?.toDate()} />;
};

export default OptionPricesCalendar;
